import { create } from "zustand"
import { devtools } from "zustand/middleware"
import { TransactionList } from "../../domain/ethereum/Models/TransactionList"
import { TransactionType } from "../../domain/ethereum/Models/Transaction"
import { DomainEventDetail, DomainEvents } from "../../domain/_kernel/Events"
import { ErrorCodes } from "../../domain/_kernel/ErrorCodes"

interface TransactionsState {
  transactions: TransactionList
  submittedTransactionType: TransactionType | null
  isSignatureSubmitting: boolean
  _areListenersActive: boolean
  _waitingTransactions: Set<string>
}

interface TransactionsActions {
  updateTransactions: () => Promise<void>
  cleanTransactions: () => Promise<void>
  setSubmittedTransactionType: (type: TransactionType | null) => void
  setIsSignatureSubmitting: (value: boolean) => void
  handleSubmitTransaction: (e: CustomEvent<DomainEventDetail[DomainEvents.SUBMIT_TRANSACTION]>) => void
  handleSubmitSignature: () => void
  handleSubmitSignatureSuccess: () => void
  handleDomainError: (e: CustomEvent<DomainEventDetail[DomainEvents.ERROR]>) => void
  handleSetTransaction: () => void
  waitForPendingTransactions: () => void
  reset: () => void
  startListening: () => void
  stopListening: () => void
}

type TransactionsStore = TransactionsState & TransactionsActions

export const useTransactionsStore = create<TransactionsStore>()(
  devtools(
    (set, get) => {
      const handleSubmitTransactionEvent = (e: CustomEvent<DomainEventDetail[DomainEvents.SUBMIT_TRANSACTION]>) => {
        get().handleSubmitTransaction(e)
      }

      const handleSubmitSignatureEvent = () => {
        get().handleSubmitSignature()
      }

      const handleDomainErrorEvent = (e: CustomEvent<DomainEventDetail[DomainEvents.ERROR]>) => {
        get().handleDomainError(e)
      }

      const handleSetTransactionEvent = () => {
        get().handleSetTransaction()
      }

      const handleSubmitSignatureSuccessEvent = () => {
        get().handleSubmitSignatureSuccess()
      }

      return {
        transactions: TransactionList.empty(),
        submittedTransactionType: null,
        isSignatureSubmitting: false,
        _areListenersActive: false,
        _waitingTransactions: new Set(),

        updateTransactions: () => {
          return window.domain.GetTransactionsEthereumUseCase.execute()
            .then(({ transactions }) => {
              set({ transactions })
              get().waitForPendingTransactions()
            })
            .catch(() => {
              set({ transactions: TransactionList.empty() })
            })
        },

        cleanTransactions: () => {
          return window.domain.CleanTransactionsEthereumUseCase.execute().then(() => {
            set({ transactions: TransactionList.empty() })
          })
        },

        setSubmittedTransactionType: (type: TransactionType | null) => {
          set({ submittedTransactionType: type })
        },

        setIsSignatureSubmitting: (value: boolean) => {
          set({ isSignatureSubmitting: value })
        },

        handleSubmitTransaction: (e: CustomEvent<DomainEventDetail[DomainEvents.SUBMIT_TRANSACTION]>) => {
          set({ submittedTransactionType: e.detail.transactionType })
        },

        handleSubmitSignature: () => {
          set({ isSignatureSubmitting: true })
        },

        handleSubmitSignatureSuccess: () => {
          set({ isSignatureSubmitting: false })
        },

        handleDomainError: (e: CustomEvent<DomainEventDetail[DomainEvents.ERROR]>) => {
          switch (e.detail.domainError.code) {
            case ErrorCodes.SIGNATURE_ERROR:
            case ErrorCodes.USER_REJECTED_SIGNATURE:
              set({ isSignatureSubmitting: false })
              break
            case ErrorCodes.TRANSACTION_ERROR:
            case ErrorCodes.USER_REJECTED_TRANSACTION:
              set({ submittedTransactionType: null })
              break
          }
        },

        handleSetTransaction: () => {
          get().updateTransactions()
          set({ submittedTransactionType: null })
        },

        waitForPendingTransactions: () => {
          const state = get()
          state.transactions.pending.forEach((transaction) => {
            const txIdentifier = transaction.hash || transaction.userOpHash
            if (!txIdentifier) return
            if (state._waitingTransactions.has(txIdentifier)) return

            state._waitingTransactions.add(txIdentifier)
            set({ _waitingTransactions: new Set(state._waitingTransactions) })

            window.domain.WaitForTransactionEthereumUseCase.execute({ transaction })
              .then(() => {
                const currentState = get()
                currentState._waitingTransactions.delete(txIdentifier)
                set({ _waitingTransactions: new Set(currentState._waitingTransactions) })
                get().updateTransactions()
              })
              .catch(() => {
                const currentState = get()
                currentState._waitingTransactions.delete(txIdentifier)
                set({ _waitingTransactions: new Set(currentState._waitingTransactions) })
              })
          })
        },

        startListening: () => {
          const state = get()
          if (state._areListenersActive) return

          set({ _areListenersActive: true })
          window.addEventListener(DomainEvents.SET_TRANSACTION, handleSetTransactionEvent)
          window.addEventListener(DomainEvents.SUBMIT_SIGNATURE, handleSubmitSignatureEvent)
          window.addEventListener(DomainEvents.ERROR, handleDomainErrorEvent as EventListener)
          window.addEventListener(DomainEvents.SUBMIT_TRANSACTION, handleSubmitTransactionEvent as EventListener)
          window.addEventListener(DomainEvents.SUCCESS_SIGNATURE, handleSubmitSignatureSuccessEvent)
        },

        reset: () => {
          set({
            transactions: TransactionList.empty(),
            submittedTransactionType: null,
            isSignatureSubmitting: false,
          })
        },

        stopListening: () => {
          const state = get()
          if (!state._areListenersActive) return

          set({ _areListenersActive: false })
          window.removeEventListener(DomainEvents.SET_TRANSACTION, handleSetTransactionEvent)
          window.removeEventListener(DomainEvents.SUBMIT_SIGNATURE, handleSubmitSignatureEvent)
          window.removeEventListener(DomainEvents.ERROR, handleDomainErrorEvent as EventListener)
          window.removeEventListener(DomainEvents.SUBMIT_TRANSACTION, handleSubmitTransactionEvent as EventListener)
          window.removeEventListener(DomainEvents.SUCCESS_SIGNATURE, handleSubmitSignatureSuccessEvent)
        },
      }
    },
    { name: "TransactionsStore" },
  ),
)
